import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { specificClientPriceApi } from '@/api/commercial/specific-client-prices';
import { NegotiationType } from '@/types/commercial/cart';
import type {
  CreateSpecificClientPricePayload,
  UpdateSpecificClientPricePayload,
} from '@/types/commercial/specific-client-prices';

export function useSpecificClientPrice(
  accountId: string | undefined,
  productId: string | undefined,
  negotiationType: NegotiationType | undefined,
  proposalId?: string,
) {
  const queryClient = useQueryClient();
  const queryKey = ['specific-client-price', accountId, productId, negotiationType];

  const isSimCard = negotiationType === NegotiationType.SIM_CARD;
  const enabled = !!accountId && !!productId && !!negotiationType;

  const { data: existingPrice, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      specificClientPriceApi.findMany({
        accountId,
        ...(isSimCard ? { connectivityId: productId } : { productId }),
        negotiationType,
        active: true,
        limit: 1,
      }),
    enabled,
    select: (results) => results[0] ?? null,
  });

  const invalidateCartQueries = () => {
    if (!proposalId) return;
    queryClient.invalidateQueries({ queryKey: ['cart-calculate', proposalId] });
    queryClient.invalidateQueries({ queryKey: ['cart-items', proposalId] });
  };

  const createMutation = useMutation({
    mutationFn: (payload: CreateSpecificClientPricePayload) =>
      specificClientPriceApi.create(payload),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey });
        invalidateCartQueries();
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSpecificClientPricePayload }) =>
      specificClientPriceApi.update(id, payload),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey });
        invalidateCartQueries();
      }
    },
  });

  const save = (
    payload:
      | { mode: 'create'; data: CreateSpecificClientPricePayload }
      | { mode: 'update'; id: string; data: UpdateSpecificClientPricePayload },
  ) => {
    if (payload.mode === 'create') {
      return createMutation.mutateAsync(payload.data);
    }
    return updateMutation.mutateAsync({ id: payload.id, payload: payload.data });
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return {
    existingPrice,
    isLoading: enabled ? isLoading : false,
    save,
    isSaving,
  };
}
